import { FC } from 'react';

/**
 * Card network acceptance marks, drawn inline so nothing is fetched at runtime
 * (no CDN, no image assets). Simplified for legibility at card-tile sizes, with
 * each mark's own proportions and colours.
 *
 * Every mark is decorative (`aria-hidden`): the text beside the tile carries the
 * brand for screen readers.
 */

type MarkProps = {
    /** Rendered width in px; height follows the mark's aspect ratio. */
    width: number;
};

const svgProps = { 'aria-hidden': true, focusable: false, style: { display: 'block' } } as const;

/** Visa wordmark — white, for a Visa-blue ground. 3 : 1. */
export const VisaMark: FC<MarkProps & { color?: string }> = ({ width, color = '#FFFFFF' }) => (
    <svg {...svgProps} width={width} height={width / 3} viewBox="0 0 48 16">
        <text
            x="24"
            y="13.9"
            textAnchor="middle"
            fontFamily="'Arial Black', 'Helvetica Neue', Arial, sans-serif"
            fontWeight={900}
            fontStyle="italic"
            fontSize="16.5"
            textLength="46"
            lengthAdjust="spacingAndGlyphs"
            fill={color}
        >
            VISA
        </text>
    </svg>
);

/**
 * Mastercard symbol — two interlocking circles, red #EB001B and yellow #F79E1B,
 * with the #FF5F00 overlap. Centres 0.62 diameters apart. 1.62 : 1.
 */
export const MastercardMark: FC<MarkProps> = ({ width }) => (
    <svg {...svgProps} width={width} height={(width * 20) / 32.4} viewBox="0 0 32.4 20">
        <circle cx="10" cy="10" r="10" fill="#EB001B" />
        <circle cx="22.4" cy="10" r="10" fill="#F79E1B" />
        <path d="M16.2 2.154A10 10 0 0 1 16.2 17.846A10 10 0 0 1 16.2 2.154Z" fill="#FF5F00" />
    </svg>
);

/**
 * American Express "blue box" — AMERICAN / EXPRESS stacked in white.
 * `boxed` draws the square blue box itself (1 : 1); unboxed is the lettering
 * alone, for a ground that is already Amex blue (5 : 3).
 */
export const AmexMark: FC<MarkProps & { boxed?: boolean }> = ({ width, boxed = false }) =>
    boxed ? (
        <svg {...svgProps} width={width} height={width} viewBox="0 0 40 40">
            <rect x="0.5" y="0.5" width="39" height="39" rx="3" fill="#016FD0" stroke="#FFFFFF" strokeOpacity="0.85" />
            <g
                fill="#FFFFFF"
                fontFamily="'Arial Narrow', 'Helvetica Neue', Arial, sans-serif"
                fontWeight={800}
                fontSize="10"
                textAnchor="middle"
            >
                <text x="20" y="18.6" textLength="33" lengthAdjust="spacingAndGlyphs">
                    AMERICAN
                </text>
                <text x="20" y="30" textLength="33" lengthAdjust="spacingAndGlyphs">
                    EXPRESS
                </text>
            </g>
        </svg>
    ) : (
        <svg {...svgProps} width={width} height={(width * 3) / 5} viewBox="0 0 40 24">
            <g
                fill="#FFFFFF"
                fontFamily="'Arial Narrow', 'Helvetica Neue', Arial, sans-serif"
                fontWeight={800}
                fontSize="9.5"
                textAnchor="middle"
            >
                <text x="20" y="10.6" textLength="36" lengthAdjust="spacingAndGlyphs">
                    AMERICAN
                </text>
                <text x="20" y="21.4" textLength="36" lengthAdjust="spacingAndGlyphs">
                    EXPRESS
                </text>
            </g>
        </svg>
    );

/** EMV chip — gold contact plate. 4 : 3. */
export const CardChip: FC<MarkProps> = ({ width }) => (
    <svg {...svgProps} width={width} height={(width * 15) / 20} viewBox="0 0 20 15">
        <rect x="0.3" y="0.3" width="19.4" height="14.4" rx="2.6" fill="#D6B25E" stroke="#8F6E25" strokeWidth="0.6" />
        <path d="M2.9 0.6H17.1A2.3 2.3 0 0 1 19.4 2.9V6H0.6V2.9A2.3 2.3 0 0 1 2.9 0.6Z" fill="#FFF3C4" opacity="0.35" />
        <path
            d="M7 0.6V14.4M13 0.6V14.4M0.6 5H7M13 5H19.4M0.6 10H7M13 10H19.4M7 7.5H13"
            fill="none"
            stroke="#8A6A22"
            strokeWidth="0.55"
        />
    </svg>
);

/** Contactless symbol — three widening arcs, in the current text colour. */
export const ContactlessMark: FC<MarkProps> = ({ width }) => (
    <svg {...svgProps} width={width} height={(width * 14) / 12} viewBox="0 0 12 14">
        <g fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round">
            <path d="M3 4.5a4 4 0 0 1 0 5" />
            <path d="M6 2.5a7 7 0 0 1 0 9" />
            <path d="M9 0.8a10 10 0 0 1 0 12.4" />
        </g>
    </svg>
);
